import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import EmptyState from '@app/src/components/EmptyState'
import Table from '@app/src/components/Table'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import { useAiSuggestionsUploadFile } from '@app/src/hooks/useAiSuggestionsUploadFile'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import FileRepoHeader from '@app/src/pages/Configurations/ConfigurationsPages/FileRepository/FileRepoHeader'
import FileRepoRow from '@app/src/pages/Configurations/ConfigurationsPages/FileRepository/FileRepoRow'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import { InfoOutlined, UploadFileOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Stack } from '@mui/material'
import React, { useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import FileRepoInfoDialog from './FileRepoInfoDialog'

export interface FileResult {
  name: string
  uploadedAt: string
}

const FileRepository: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { formatMessage } = useIntl()
  const { isWaitingToReload, isUploading, uploadFile } = useAiSuggestionsUploadFile()
  const [open, openDialog, closeDialog] = useDialogState()
  const { trackEvent } = useAmplitude()

  const {
    data: files,
    isLoading,
    isError,
  } = useFetchResource<FileResult[]>({
    endpoint: endpoints.suggestionFiles,
    key: FetchKey.FileRepository,
  })

  const sortedFiles = useMemo(
    () => files?.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()) ?? [],
    [files],
  )

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile) {
      await uploadFile(selectedFile)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleLearnMoreClick = () => {
    trackEvent({
      name: AmplitudeTrackingEvents.Dashboard.PrepareForRequest.LearnMoreDialogOpened,
    })
    openDialog()
  }

  return (
    <ConfigurationCollection
      actionButtons={[
        {
          label: formatMessage({ id: 'general.learnMore' }),
          onClick: handleLearnMoreClick,
          startIcon: <InfoOutlined />,
        },
        {
          label: formatMessage({ id: 'schemas.repositoryFile.upload' }),
          onClick: handleFileClick,
          variant: 'contained',
          loading: isUploading,
          startIcon: <UploadFileOutlined />,
        },
      ]}
      enablePadding={{ right: true, top: false }}
    >
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
      <Table<FileResult>
        HeaderComponent={() => <FileRepoHeader />}
        RowComponent={FileRepoRow}
        data={sortedFiles}
        isLoading={isLoading || isUploading || isWaitingToReload}
        isError={isError}
        count={10000}
        page={1}
        pageSize={10000}
        noPagination
        emptyState={
          <EmptyState
            title={formatMessage({ id: 'schemas.repositoryFile.emptyStateTitle' })}
            iconImages={['/img/ai-repo/_Avatar_ (2).png', '/img/ai-repo/_Avatar_.png', '/img/ai-repo/_Avatar_ (1).png']}
            description={formatMessage({ id: 'schemas.repositoryFile.emptyStateDescription' })}
            negative
          >
            <Stack direction="row" spacing={2}>
              <LoadingButton onClick={handleLearnMoreClick}>
                {formatMessage({ id: 'schemas.repositoryFile.aiUsageInfo' })}
              </LoadingButton>
              <LoadingButton
                onClick={handleFileClick}
                loading={isUploading}
                startIcon={<UploadFileOutlined />}
                variant="contained"
              >
                {formatMessage({ id: 'schemas.repositoryFile.upload' })}
              </LoadingButton>
            </Stack>
          </EmptyState>
        }
      />
      <FileRepoInfoDialog open={open} closeDialog={closeDialog} />
    </ConfigurationCollection>
  )
}

export default FileRepository
